import { useEffect, useMemo, useState } from 'react';
import { Badge, Nav } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useExpanded, useFilters, usePagination, useRowSelect, useTable } from 'react-table';
import { toast } from 'react-toastify';
import { TableSelect } from '../../../../../../Components/ReactTable/components/TableSelect';
import DefaultColumnFilter from '../../../../../../Components/ReactTable/Filters/DefaultColumnFilter';
import ReactTableRender from '../../../../../../Components/ReactTable/ReactTableRender';
import { ThematicAreaBadge } from '../../../../../../Components/ThematicAreaBadge';
import { TargetedParty } from '../../../../../../Pages/admin/Events/EventsTable/components/style';
import { handleAppliedEvent } from '../../../../../../Redux/slices/event';
import { handleResultsChange } from '../../../../../../Redux/slices/table';
import { fetchWrapper } from '../../../../../../_helpers/fetchWrapper';
import { ErrorMessageHandler } from '../../../../../../_helpers/_methods';

const TargetedForSelectColumnFilter = ({
  column: { filterValue, setFilter, preFilteredRows, id }
}) => {
  const options = useMemo(() => {
    const options = new Set();
    if (preFilteredRows) {
      preFilteredRows.forEach((row) => {
        row.values[id]?.forEach((value) => options.add(value.title));
      });
    }
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <TableSelect
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}>
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </TableSelect>
  );
};

const ThemanticAreaSelectColumnFilter = ({
  column: { filterValue, setFilter, preFilteredRows, id }
}) => {
  const options = useMemo(() => {
    const options = new Set();
    if (preFilteredRows) {
      preFilteredRows.forEach((row) => {
        row.values[id]?.forEach((value) => options.add(value.title_nepali));
      });
    }
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <TableSelect
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}>
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </TableSelect>
  );
};

const options = [
  {
    value: 'upcoming',
    label: 'upcomingEvents'
  },
  {
    value: 'ongoing',
    label: 'ongoingEvents'
  },
  {
    value: 'completed',
    label: 'completedEvents'
  }
];
function EventsTableTabs({ type, isHomePage }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pageNum = useSelector((state) => state.table.pageNum);
  const lang = useSelector((state) => state.local.local);
  const user = useSelector((state) => state.user.user);
  const loading = useSelector((state) => state.table.loading);
  const totalCount = useSelector((state) => state.table.totalCount);
  const results = useSelector((state) => state.table.results);
  const [activeTab, setActiveTab] = useState(type || 'upcoming');
  const [filteredEvents, setFilteredEvents] = useState([]);

  useEffect(() => {
    setFilteredEvents(results);
  }, [results]);

  const columns = useMemo(
    () => [
      {
        Header: <FormattedMessage id="Events" />,
        accessor: 'eventName',
        Cell: ({ row: { original } }) => (
          <span className="d-block">
            <Link to={isHomePage ? `/events/${original.id}` : `${original.id}`}>
              <strong className="text-primary">{original.eventName}</strong>
            </Link>

            <span className="d-block small text-secondary mb-2">
              {original.start_date} to {original.end_date}
            </span>
            <span className="d-block">
              {original.mtot ? (
                <span className="bg-success text-light py-1 px-2 rounded small">MTOT event</span>
              ) : null}
            </span>
          </span>
        )
      },

      {
        Header: <FormattedMessage id="event.targeted_for" />,
        accessor: 'targeted_for',
        Filter: TargetedForSelectColumnFilter,
        filter: 'includes',
        Cell: ({ cell: { value } }) => (
          <span className="d-flex align-items-center flex-wrap">
            {value && (
              <>
                <ul>
                  {value.slice(0, 3).map((target, index) => (
                    <TargetedParty key={index}>
                      {target?.title} {target?.parent__title ? `(${target?.parent__title})` : ''}
                    </TargetedParty>
                  ))}
                  {value.length > 3 ? (
                    <span className="small text-secondary">+{value.length - 3} more</span>
                  ) : null}
                </ul>
              </>
            )}
          </span>
        )
      },
      {
        Header: <FormattedMessage id="event.organized_by" />,
        accessor: 'organizer',
        Cell: ({ cell: { value } }) => (
          <span>
            <span className="d-block">{value?.employee_type}</span>
            <span className="d-block">{value?.province}</span>
            <span className="d-block">{value?.user}</span>
          </span>
        )
      },
      {
        Header: <FormattedMessage id="thematic_area" />,
        accessor: 'thematic_areas',
        Filter: ThemanticAreaSelectColumnFilter,
        filter: 'includes',
        Cell: ({ row, cell: { value } }) => (
          <span className="d-flex align-items-center flex-wrap">
            {Boolean(value) && (
              <ul>
                {row.isExpanded ? (
                  <div {...row.getToggleRowExpandedProps()}>
                    <span>👇</span>
                    <div>
                      {value.map((target, index) => (
                        <ThematicAreaBadge key={index}>
                          {lang === 'ne'
                            ? target.title_nepali
                            : target.title_english
                            ? target.title_english
                            : target.title_nepali}
                        </ThematicAreaBadge>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {value.slice(0, 3).map((target) => (
                      <ThematicAreaBadge key={target.id}>
                        {lang === 'ne'
                          ? target.title_nepali
                          : target.title_english
                          ? target.title_english
                          : target.title_nepali}
                      </ThematicAreaBadge>
                    ))}
                    {value.length > 3 ? (
                      <span className="small text-secondary" {...row.getToggleRowExpandedProps()}>
                        +{value.length - 3} more 👉
                      </span>
                    ) : null}
                  </>
                )}
              </ul>
            )}
          </span>
        )
      },
      {
        Header: <FormattedMessage id="events.targeted_areas" defaultMessage="Targeted Areas" />,
        accessor: 'targeted_areas',
        Cell: ({ cell: { value } }) => (
          <span>
            {value?.map((val) => {
              return (
                <span key={val.type} className="border border-radius d-block p-1 mb-1">
                  <span className="d-block">{val.type}</span>
                  <span>
                    {val.provinces
                      ? val.provinces.map((province) => (
                          <Badge key={province.id} className="me-1" bg="primary">
                            {lang === 'ne' ? province.title : province.title_en}
                          </Badge>
                        ))
                      : null}
                  </span>
                  <span>
                    {val.local_level
                      ? val.local_level.map((lol) => (
                          <Badge key={lol.id} className="me-1" bg="primary">
                            {lang === 'ne' ? lol.title : lol.title_en}
                          </Badge>
                        ))
                      : null}
                  </span>
                </span>
              );
            })}
          </span>
        )
      },
      {
        Header: <FormattedMessage id="location" />,
        accessor: 'location',
        Cell: ({ cell: { value } }) => (
          <span className="d-block">
            <span className="d-block">{value.at}</span>
            <span className="d-block">
              {value.virtual_link ? <span className="text-primary">Virtual</span> : null}
            </span>
          </span>
        )
      },
      {
        Header: <FormattedMessage id="actions" />,
        accessor: 'actions',
        disableFilters: true,
        width: '200px',
        Cell: ({ row: { original } }) => (
          <div className="d-flex center flex-column ">
            <Link to={`/events/${original.id}`} state={{ from: activeTab }}>
              <button className="btn btn-sm btn-outline-success mb-2" style={{ minWidth: '140px' }}>
                <FormattedMessage id="view" />
              </button>
            </Link>
            {activeTab === 'upcoming' ? (
              user === null || ['participant', 'resource_person'].includes(user?.active_role) ? (
                <button
                  className="btn btn-sm btn-outline-primary mb-2"
                  style={{ minWidth: '140px' }}
                  onClick={(e) => handleApply(e, original, user)}>
                  <FormattedMessage id="apply" />
                </button>
              ) : null
            ) : null}
          </div>
        )
      }
    ],
    [lang, activeTab, user]
  );

  const data = useMemo(() => {
    if (!filteredEvents) return [];
    return filteredEvents.map((result) => {
      return {
        id: result.id,
        eventName: lang === 'ne' ? result.title_nepali : result.title_english,
        english_name: result.title_english,
        nepali_name: result.title_nepali,
        start_date: result.start_date_bs,
        end_date: result.end_date_bs,
        mtot: result.mtot,

        targeted_for: result.expected_participant_sector,
        organizer: result.created_by,
        thematic_areas: result.thematic_areas,
        targeted_areas: result.targeted_local_gov,
        location: {
          virtual_link: result.virtual_link,
          at: `${result?.physical_location_details?.tole || ''} ${
            result?.physical_location_details?.ward || ''
          } ${result?.physical_location_details?.municipality || ''} ${
            result?.physical_location_details?.district || ''
          } ${result?.physical_location_details?.province || ''}`
        }
      };
    });
  }, [lang, filteredEvents]);

  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter
    }),
    []
  );

  const {
    headerGroups,
    getTableProps,
    getTableBodyProps,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    visibleColumns,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 50 },
      manualPagination: true,
      pageCount: pageNum,
      defaultCanFilter: false,
      defaultColumn
      // manualFilters: true
    },
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect
  );
  const pageCountArray = useMemo(
    () => Array.from({ length: Math.ceil(totalCount / pageSize) }, (v, i) => i + 1),
    [totalCount]
  );
  useEffect(() => {
    dispatch(
      handleResultsChange({
        index: pageIndex,
        eventState: activeTab,
        pageSize: pageSize,
        eventType: isHomePage ? 'public/open' : null
      })
    );
  }, [pageIndex, activeTab, pageSize]);
  const handleActiveTabChange = (value) => {
    if (type) {
      navigate(`/admin/report/overall/${value}`);
    }
    setActiveTab(value);
  };
  const handleApply = async (e, event, user) => {
    if (!user) {
      dispatch(handleAppliedEvent(event.id));
      navigate('/signin');
    } else {
      try {
        await fetchWrapper.post(`/event/${event.id}/participate/`, '', '', lang);
        dispatch(
          handleResultsChange({
            index: pageIndex,
            eventState: activeTab,
            pageSize: pageSize,
            eventType: isHomePage ? 'public/open' : null
          })
        );
        toast.success(<FormattedMessage id="applied_to_event" />);
      } catch (error) {
        ErrorMessageHandler(error);
      }
    }
  };

  return (
    <div>
      <Nav variant="tabs" defaultActiveKey={activeTab}>
        {options.map((item) => (
          <Nav.Item key={item.value} onClick={() => handleActiveTabChange(item.value)}>
            <Nav.Link eventKey={item.value} className="h-100">
              <div
                className={`font-weight-bold m-0 ${
                  item.value === activeTab ? 'text-primary' : ''
                }`}>
                <FormattedMessage id={item.label} />
              </div>
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      <ReactTableRender
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        getTableBodyProps={getTableBodyProps}
        page={page}
        prepareRow={prepareRow}
        totalCount={totalCount}
        gotoPage={gotoPage}
        previousPage={previousPage}
        pageCount={pageCount}
        pageCountArray={pageCountArray}
        pageNum={pageNum}
        showGoto={true}
        nextPage={nextPage}
        pageIndex={pageIndex}
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        loading={loading}
        visibleColumns={visibleColumns}
        pagination={true}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </div>
  );
}

export default EventsTableTabs;
